import { useTranslation } from 'react-i18next';
import LtDialog from '../LtDialog';
import { Link } from '@lemontaps/lib/types/api';
import { useEffect, useState } from 'react';
import { Box, Button } from '@mui/material';
import { AddEditLinkDialogProps } from './types';
import { DeleteOutline } from '@mui/icons-material';
import { AddEditUserLinkForm } from './AddEditUserLinkForm';
import { useEditRights } from '@/infrastructure/hooks/useEditRights';
import { THEME_CONFIG_KEYS, THEME_EDIT_RIGHT_MODES } from '@lemontaps/lib/constants';
import usePrivateThemeConfig from '@/infrastructure/hooks/usePrivateThemeConfig';

export const AddEditUserLinkDialog = ({
  linkTypes,
  onClose,
  open,
  initialValues,
  onSave,
  templates,
  loading,
  onDelete,
}: AddEditLinkDialogProps) => {
  const [values, setValues] = useState<Partial<Link>>(initialValues || {});
  const selectedLinkType = linkTypes.find(lt => lt.id === values.linkTypeId);
  const isEdit = Boolean(values.id);
  const { t } = useTranslation();

  const { config: approvalRequiredLinkTypes } = usePrivateThemeConfig<string[]>(
    THEME_CONFIG_KEYS.REQUIRES_APPROVAL_LINK_TYPES,
  );

  const {
    editRights: { linkEditMode },
  } = useEditRights();
  const requiresApproval =
    linkEditMode === THEME_EDIT_RIGHT_MODES.ON_APPROVAL ||
    approvalRequiredLinkTypes?.includes(selectedLinkType?.uniqueId || '');

  useEffect(() => {
    if (!initialValues) return;
    setValues(initialValues);
  }, [initialValues]);

  /** need timeout to keep smooth dialog close/open animation */
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (!open) {
      timeoutId = setTimeout(() => setValues({}), 500);
      return () => clearTimeout(timeoutId);
    }
  }, [open]);

  const handleSubmit = () => {
    const payload = { ...values };

    /**
     * If the selected templateId is -1, remove the linkTemplateId from the payload
     */
    if (payload.linkTemplateId === -1) payload.linkTemplateId = null;
    onSave(payload);
  };

  return (
    <LtDialog
      open={open}
      onClose={onClose}
      onCancel={onClose}
      saveAction={{
        text: requiresApproval ? t('requestApproval') : t('save'),
        onClick: handleSubmit,
        disabled: !values.linkTypeId || !values.link || !values.name,
        loading,
      }}
      title={initialValues ? t('editLink') : t('createLink')}
      size='sm'
      withActionDivider
    >
      <AddEditUserLinkForm
        linkTypes={linkTypes}
        values={values}
        onChange={setValues}
        templates={templates}
        approvalRequiredLinkTypes={approvalRequiredLinkTypes || []}
      />

      <Box mt={2} display='flex'>
        {onDelete && isEdit && (
          <Button
            sx={{ width: 'max-content', ml: 'auto' }}
            variant='text'
            startIcon={<DeleteOutline />}
            onClick={() => values.id && onDelete(values.id)}
          >
            {t('deleteLink')}
          </Button>
        )}
      </Box>
    </LtDialog>
  );
};
